import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControl,
    FormControlLabel,
    Checkbox,
    Typography,
    Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNotification } from 'context/NotificationContext';
import styles from "./styles.module.scss"

const maxPromptLength = 1500;

const validationSchema = Yup.object({
    requirements: Yup.string().max(maxPromptLength),
    expectedTestCases: Yup.string().max(maxPromptLength),
});

const CreateTestCaseModal = ({
    open,
    onClose,
    userRequirements = null,
    userExpectedTestCases = null,
    onGenerate,
    isGeneration,
    mode = "generate",
}) => {
    const showNotification = useNotification()
    const isRegenerateMode = mode === "regenerate"
    const initialRequirements = userRequirements ?? "";
    const initialExpectedTestCases = userExpectedTestCases ?? "";

    const onError = () => {
        showNotification("Error! Something went wrong, please try again later.", "error", "", null);
    }

    const handleCreateWithoutPrompt = async (includeSubJourneys, isRandomData) => {
        try {
            console.log("[Journey Generate] isRandomData (no context):", isRandomData);
            const contextData = {
                include_sub_journeys: includeSubJourneys,
                is_random_data: isRandomData ?? true,
            };
            await onGenerate(contextData);
        }
        catch (err) {
            onError();
        }
        finally {
            onClose();
        }
    };

    const handleSubmit = async (values) => {
        try {
            const { requirements, expectedTestCases, includeSubJourneys, isRandomData } = values;
            console.log("[Journey Generate] isRandomData (with context):", isRandomData);
            const contextData = {
                user_requirements: requirements?.trim() || null,
                user_expected_test_cases: expectedTestCases?.trim() || null,
                include_sub_journeys: includeSubJourneys,
                is_random_data: isRandomData ?? true,
            };
            
            await onGenerate(contextData);
        }
        catch (error) {
            onError();
        }
        finally {
            onClose();
        }
    }

    return (
        <Dialog 
            className={styles.content} 
            open={open} 
            onClose={onClose} 
            sx={{
                '& .MuiDialog-paper': {
                    width: '1000px',
                    maxWidth: '1000px',
                }
            }}
        >
            <DialogTitle className={styles.title}>
                Generate Test Cases from Journey
            </DialogTitle>

            <Formik
                enableReinitialize
                initialValues={{ 
                    requirements: initialRequirements, 
                    expectedTestCases: initialExpectedTestCases,
                    includeSubJourneys: false,
                    isRandomData: true,
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({ values, errors, handleChange, handleBlur, isSubmitting }) => {
                    const isAnyGenerating = isSubmitting || isGeneration;
                    const loadingLabel = isRegenerateMode ? 'Regenerating...' : 'Generating...';
                    const withoutContextLabel = isRegenerateMode ? 'REGENERATE WITHOUT CONTEXT' : 'GENERATE WITHOUT CONTEXT';
                    const withContextLabel = isRegenerateMode ? 'REGENERATE WITH CONTEXT' : 'GENERATE WITH CONTEXT';
                    return (
                        <Form>
                            <DialogContent className={styles.formContent}>
                                <Typography className={styles.description}>
                                    Add a bit more context about the user journey you&apos;ve just recorded and what you expect from the test cases. This will help Treegress better understand your application&apos;s logic and generate more accurate tests.
                                </Typography>
                                
                                <FormControl fullWidth margin="dense">
                                    <Box className={styles.textInputWrapper}>
                                        <TextField
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    paddingBottom: "32px",
                                                }
                                            }}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={12}
                                            label="Requirements"
                                            placeholder='Add user stories or requirements for this journey. Describe how this functionality is supposed to work. Example: User can submit a "Contact Us" form with name, email, and message, then sees a success message.'
                                            name="requirements"
                                            margin="dense"
                                            value={values.requirements}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(errors.requirements)}
                                            helperText={
                                                <>
                                                    {errors.requirements}
                                                    <span className={styles.descriptionCounter}>
                                                        {`${values?.requirements?.length || 0} / ${maxPromptLength}`}
                                                    </span>
                                                </>
                                            }
                                        />
                                    </Box>
                                </FormControl>

                                <FormControl fullWidth margin="dense">
                                    <Box className={styles.textInputWrapper}>
                                        <TextField
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    paddingBottom: "32px",
                                                }
                                            }}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={12}
                                            label="Expected test cases"
                                            placeholder="Describe what you want the test cases to cover and verify for this journey. Example: Check that form can't be submitted with empty fields, invalid emails are rejected, success message appears after valid submit."
                                            name="expectedTestCases"
                                            margin="dense"
                                            value={values.expectedTestCases}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(errors.expectedTestCases)}
                                            helperText={
                                                <>
                                                    {errors.expectedTestCases}
                                                    <span className={styles.descriptionCounter}>
                                                        {`${values?.expectedTestCases?.length || 0} / ${maxPromptLength}`}
                                                    </span>
                                                </>
                                            }
                                        />
                                    </Box>
                                </FormControl>

                                <FormControl fullWidth margin="dense">
                                    <FormControlLabel
                                        className={styles.checkboxControl}
                                        control={
                                            <Checkbox
                                                name="isRandomData"
                                                checked={values.isRandomData}
                                                onChange={handleChange}
                                                color="secondary"
                                            />
                                        }
                                        label={
                                            <Box className={styles.checkboxLabel}>
                                                <Typography
                                                    component="span"
                                                    className={styles.checkboxLabelText}
                                                >
                                                    Generate test cases with random data.
                                                </Typography>
                                                <Tooltip
                                                    title="Uses random test data on each run for better reliability and coverage. Disable to reuse the recorded data."
                                                    arrow
                                                >
                                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                </FormControl>

                                <FormControl fullWidth margin="dense">
                                    <FormControlLabel
                                        className={styles.checkboxControl}
                                        control={
                                            <Checkbox
                                                name="includeSubJourneys"
                                                checked={values.includeSubJourneys}
                                                onChange={handleChange}
                                                color="secondary"
                                            />
                                        }
                                        label={
                                            <Box className={styles.checkboxLabel}>
                                                <Typography
                                                    component="span"
                                                    className={styles.checkboxLabelText}
                                                >
                                                    Include sub-journeys
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </FormControl>
                            </DialogContent>

                            <DialogActions className={styles.actionWrapper}>
                                <Button
                                    color="secondary"
                                    className={styles.button}
                                    onClick={onClose}
                                    type="button"
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="secondary"
                                    className={styles.button}
                                    disabled={isAnyGenerating}
                                    type="button"
                                    variant="outlined"
                                    onClick={() => handleCreateWithoutPrompt(values.includeSubJourneys, values.isRandomData)}
                                >
                                    {isAnyGenerating ? loadingLabel : withoutContextLabel}
                                </Button>
                                <Button
                                    color="secondary"
                                    className={styles.button}
                                    disabled={isAnyGenerating || (!values?.requirements && !values?.expectedTestCases) || errors.requirements || errors.expectedTestCases}
                                    type="submit"
                                    variant="contained">
                                    {isAnyGenerating ? loadingLabel : withContextLabel}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default CreateTestCaseModal;
